import React from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';

import { DashboardScrollView } from '@/components/dashboard-scroll-view';
import { ThemedText } from '@/components/themed-text';
import { EmptyState } from '@/components/ui/empty-state';
import { PressableCard } from '@/components/ui/card';
import { useBookableServices } from '@/hooks/use-appointments';
import { BookableService } from '@/types/appointment';
import { formatDuration } from '@/utils/format-appointment';
import { useIconChip } from '@/hooks/use-theme-colors';

const BRAND = '#FD5001';

function ServiceRow({ service }: { service: BookableService }) {
  const chip = useIconChip();
  return (
    <PressableCard
      onPress={() =>
        router.push({
          pathname: '/(tabs)/(client)/service/[serviceId]',
          params: { serviceId: service.id },
        })
      }
      className="w-full flex-row items-center gap-4 p-4"
    >
      <View
        className="h-11 w-11 items-center justify-center rounded-2xl"
        style={{ backgroundColor: chip(BRAND, '1A').bg, borderCurve: 'continuous' }}
      >
        <Ionicons name="pricetag-outline" size={18} color={chip(BRAND, '1A').fg} />
      </View>
      <View className="min-w-0 flex-1 gap-0.5">
        <ThemedText className="font-semibold text-foreground" numberOfLines={1}>
          {service.name}
        </ThemedText>
        <View className="flex-row items-center gap-2">
          {!!service.durationMinutes && (
            <ThemedText className="text-sm text-neutral-500">
              {formatDuration(service.durationMinutes)}
            </ThemedText>
          )}
          {service.slotCapacity > 1 && (
            <ThemedText className="text-sm text-neutral-500">
              · turma de {service.slotCapacity}
            </ThemedText>
          )}
        </View>
      </View>
      <Ionicons className="text-neutral-400" name="chevron-forward" size={18} />
    </PressableCard>
  );
}

export default function ProviderServicesScreen() {
  const { providerId, name } = useLocalSearchParams<{
    providerId: string;
    name?: string;
  }>();
  const { data, isLoading } = useBookableServices(providerId);

  return (
    <View className="flex-1 bg-canvas">
      <DashboardScrollView
        className="w-full"
        contentContainerStyle={{ paddingBottom: 80 }}
      >
        <View className="gap-6 p-5">
          <View className="flex-row items-center gap-3">
            <Pressable
              onPress={() => router.back()}
              className="h-10 w-10 items-center justify-center rounded-full bg-card"
              accessibilityLabel="Voltar"
            >
              <Ionicons className="text-neutral-400" name="arrow-back" size={20} />
            </Pressable>
            <View className="flex-1">
              <ThemedText className="text-xl font-bold" numberOfLines={1}>
                {name || 'Serviços'}
              </ThemedText>
              <ThemedText className="text-sm text-neutral-500">
                Escolha um serviço para agendar
              </ThemedText>
            </View>
          </View>

          {isLoading ? (
            <View className="items-center py-16">
              <ActivityIndicator size="large" color={BRAND} />
            </View>
          ) : (data?.length ?? 0) === 0 ? (
            <EmptyState
              icon="pricetags-outline"
              title="Nenhum serviço agendável"
              description="Este prestador ainda não configurou serviços para agendamento online."
            />
          ) : (
            <View className="gap-3">
              {data!.map((s) => (
                <ServiceRow key={s.id} service={s} />
              ))}
            </View>
          )}
        </View>
      </DashboardScrollView>
    </View>
  );
}
